import { RefObject, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowUp, AudioLines, Check, ChevronDown, CircleCheck, CircleX, Clock1, ClockAlert, FileStack, Globe, Image, Mic, MicOff, Palette, Radio, RectangleHorizontal, RectangleVertical, SlidersHorizontal, Square, Telescope, TriangleAlert, X } from 'lucide-react';
import { isEmpty } from 'lodash-es';

import { ChatModel } from '@/api/models';
import { AppStateContext } from '@/state/AppStateContext';
import { textareaRef } from '@/components/PromptInput/promptInputRef';
import { transcribe } from '@/api/api';
import { changeModel, setModelForMessage, doTrackEvent, getLogo, selectTool } from '@/utils/utils';
import { FILE_UPLOAD_CONSTRAINTS, validateFiles, validateImage, createFileList, showFileValidationErrors, showImageValidationError, getAllowedFileMimeTypes, getAllowedFileTypes, getFileIcon } from '@/utils/fileValidation';
import { FileSnapshotError, snapshotFile, snapshotFiles } from '@/utils/fileSnapshot';
import DictationIndicator from '@/components/PromptInput/DictationIndicator';
import ModelSelector from '@/components/PromptInput/ModelSelector';
import PopupMenu from '@/components/PromptInput/PopupMenu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import microsoft365 from "@/assets/microsoft365.png";

const MAX_RECORDING_MS = 5 * 60 * 1000;

const formatNextWeeklyReset = () => {
  const now = new Date();
  const reset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  reset.setUTCDate(reset.getUTCDate() + ((1 - reset.getUTCDay() + 7) % 7 || 7));

  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(reset);
};

const IMAGE_SIZE_OPTIONS = [
  {
    value: '1024x1024',
    label: 'Square',
    description: '1024 x 1024',
    Icon: Square
  },
  {
    value: '1024x1536',
    label: 'Portrait',
    description: '1024 x 1536',
    Icon: RectangleVertical
  },
  {
    value: '1536x1024',
    label: 'Landscape',
    description: '1536 x 1024',
    Icon: RectangleHorizontal
  }
] as const;

const IMAGE_STYLE_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: 'Cinematic', label: 'Cinematic' },
  { value: 'Flat', label: 'Flat' },
  { value: 'Isometric 3D', label: 'Isometric 3D' },
  { value: 'Photorealistic', label: 'Photorealistic' },
  { value: 'Sketch', label: 'Sketch' },
  { value: 'Surrealism', label: 'Surrealism' },
  { value: 'SMS', label: 'SMS' }
] as const;

interface PromptInputProps {
  placeholder?: string;
  disabled: boolean;
  question: string;
  setQuestion: (question: string) => void;
  sendPrompt: (question: string, conversationId?: string) => void;
  handleAbort: () => void;
  conversationId?: string;
  imageSize: string;
  setImageSize: (size: string) => void;
  imageStyle: string;
  setImageStyle: (style: string) => void;
  marketingSearchLevel: string;
  setMarketingSearchLevel: (level: string) => void;
  isGroupChat?: boolean;
  longConversation?: boolean;
  onOpenVoicePanel?: () => void;
  voiceModeEnabled?: boolean;
  isVoiceSessionActive?: boolean;
}

const doesTextareaWrapAtWidth = (textarea: HTMLTextAreaElement, width: number) => {
  const measurement = document.createElement('textarea');
  const computedStyle = window.getComputedStyle(textarea);
  const propertiesToCopy = [
    'box-sizing',
    'padding-top',
    'padding-right',
    'padding-bottom',
    'padding-left',
    'border-top-width',
    'border-right-width',
    'border-bottom-width',
    'border-left-width',
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'font-kerning',
    'font-stretch',
    'font-variant',
    'letter-spacing',
    'line-height',
    'text-indent',
    'text-transform',
    'tab-size',
    'white-space',
    'word-break',
    'overflow-wrap'
  ];

  measurement.setAttribute('aria-hidden', 'true');
  measurement.rows = 1;
  measurement.style.position = 'absolute';
  measurement.style.visibility = 'hidden';
  measurement.style.pointerEvents = 'none';
  measurement.style.zIndex = '-1';
  measurement.style.top = '0';
  measurement.style.left = '0';
  measurement.style.height = '0';
  measurement.style.minHeight = '0';
  measurement.style.maxHeight = 'none';
  measurement.style.overflow = 'hidden';
  measurement.style.resize = 'none';
  measurement.style.width = `${width}px`;

  propertiesToCopy.forEach((property) => {
    measurement.style.setProperty(property, computedStyle.getPropertyValue(property));
  });

  document.body.appendChild(measurement);

  measurement.value = ' ';
  const singleLineHeight = measurement.scrollHeight;

  measurement.value = textarea.value || ' ';
  const contentHeight = measurement.scrollHeight;

  document.body.removeChild(measurement);

  return contentHeight > singleLineHeight + 1;
};

const PromptInput = ({ placeholder, disabled, question, setQuestion, sendPrompt, handleAbort, conversationId, imageSize, setImageSize, imageStyle, setImageStyle, marketingSearchLevel, setMarketingSearchLevel, isGroupChat, longConversation = false, onOpenVoicePanel, voiceModeEnabled = false, isVoiceSessionActive = false } : PromptInputProps) => {
  const appStateContext = useContext(AppStateContext);
  const weeklyUsage = appStateContext?.state.weeklyUsage;
  const hasExhaustedWeeklyTokens = Boolean(
    weeklyUsage && weeklyUsage.limit > 0 && weeklyUsage.used >= weeklyUsage.limit
  );
  const navigate = useNavigate();
  const location = useLocation();
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const handleGetLogo = (brand: string) => getLogo(appStateContext, brand);
  const handleModelChange = (model: ChatModel) => {
    if (isGroupChat) {
      setModelForMessage(appStateContext, model);
      return;
    }
    changeModel(appStateContext, model);
    if (location.pathname !== '/') navigate('/');
  };
  // Reasoning effort applies to the next message only, so it must not reset the conversation.
  const handleEffortChange = (model: ChatModel) => setModelForMessage(appStateContext, model);
  const handleSelectTool = (tool: string) => selectTool(appStateContext, tool);
  const isImageGenerationSelected = appStateContext?.state.selectedTool == 'image_generation';
  const isImageGenerationMode = isImageGenerationSelected;
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const pendingFilesRef = useRef<File[]>([]);
  const pendingFilePreparationCountRef = useRef(0);
  const fileSelectionRevisionRef = useRef(0);
  const imageSelectionRevisionRef = useRef(0);
  const isMountedRef = useRef(true);
  const fileUploadStateRef = useRef({
    click: () => fileUploadRef.current?.click(),
    get files() {
      return createFileList(pendingFilesRef.current).files;
    },
    get isPreparing() {
      return pendingFilePreparationCountRef.current > 0;
    },
    get fileSelectionRevision() {
      return fileSelectionRevisionRef.current;
    },
    get imageSelectionRevision() {
      return imageSelectionRevisionRef.current;
    },
    beginPreparation() {
      pendingFilePreparationCountRef.current++;
      appStateContext?.dispatch({ type: 'SET_FILE_UPLOAD', payload: fileUploadStateRef });
    },
    endPreparation() {
      pendingFilePreparationCountRef.current = Math.max(0, pendingFilePreparationCountRef.current - 1);
      if (isMountedRef.current) appStateContext?.dispatch({ type: 'SET_FILE_UPLOAD', payload: fileUploadStateRef });
    },
    invalidateFilePreparations() {
      fileSelectionRevisionRef.current++;
    },
    invalidateImagePreparations() {
      imageSelectionRevisionRef.current++;
    },
    set files(files: FileList | null) {
      pendingFilesRef.current = files ? Array.from(files) : [];
    },
    get value() {
      return fileUploadRef.current?.value ?? '';
    },
    set value(value: string) {
      if (fileUploadRef.current) fileUploadRef.current.value = value;
      if (value === '') {
        pendingFilesRef.current = [];
        fileSelectionRevisionRef.current++;
      }
    }
  });
  const imageUploadRef = useRef<HTMLInputElement>(null);
  const [recording, setRecording] = useState<boolean>(false);
  const [processingRecording, setProcessingRecording] = useState<boolean>(false);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [hasMultipleLines, setHasMultipleLines] = useState<boolean>(false);
  const [isImageSizeMenuOpen, setIsImageSizeMenuOpen] = useState<boolean>(false);
  const [isImageStyleMenuOpen, setIsImageStyleMenuOpen] = useState<boolean>(false);
  const [isMarketingMenuOpen, setIsMarketingMenuOpen] = useState<boolean>(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cancelledRecordingRef = useRef<boolean>(false);
  const dictationPendingFocusRef = useRef<boolean>(false);
  const audioChunksRef = useRef<Blob[]>([]);
  const compactTextareaWidthRef = useRef<number | null>(null);
  const selectedTool = appStateContext?.state.selectedTool ?? '';
  const uploadedFiles = Array.from<File>(appStateContext?.state.fileUpload?.current?.files ?? []);
  const uploadedImages = Array.from<File>(appStateContext?.state.imageUpload?.current?.files ?? []);
  const isPreparingFileUpload = Boolean(appStateContext?.state.fileUpload?.current?.isPreparing);
  const hasUploads = uploadedFiles.length > 0 || uploadedImages.length > 0;
  const isDictating = recording || processingRecording;
  const isExpanded = hasMultipleLines || !isEmpty(selectedTool) || hasUploads || (!isEmpty(appStateContext?.state.currentCustomGPT) && appStateContext?.state.currentCustomGPT.id == 'e7e6d46d-dd49-4a08-b02a-4499de9d540a') || !isEmpty(appStateContext?.state.currentAgent);

  /** 
   * Set ref to file upload input
  **/
  useEffect(() => {
    if (fileUploadRef.current) appStateContext?.dispatch({ type: 'SET_FILE_UPLOAD', payload: fileUploadStateRef });
  }, [fileUploadRef.current]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      fileSelectionRevisionRef.current++;
      imageSelectionRevisionRef.current++;
    };
  }, []);

  /** 
   * Set ref to image upload input
  **/
  useEffect(() => {
    if (imageUploadRef.current) appStateContext?.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: imageUploadRef });
  }, [imageUploadRef.current]);

  /** 
   * Handle prompt input textarea height adjustment
  **/
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.overflow = 'hidden';

    // Nothing to size to while empty, and `scrollHeight` never reports less than the
    // element's current height, so measuring here can latch in a too-tall composer
    // (e.g. when remounting on a route change). Fall back to the class-based height.
    if (!question) {
      textarea.style.height = '';
      setHasMultipleLines(false);
      return;
    }

    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 400);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflow = newHeight >= 400 ? 'auto' : 'hidden';

    const currentTextareaWidth = textarea.clientWidth;
    if (!isExpanded && currentTextareaWidth > 0) compactTextareaWidthRef.current = currentTextareaWidth;

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(computedStyle.lineHeight || '24') || 24;
    const multilineMeasurementWidth = compactTextareaWidthRef.current ?? currentTextareaWidth;
    const wrapsCompactTextarea = multilineMeasurementWidth > 0
      ? doesTextareaWrapAtWidth(textarea, multilineMeasurementWidth)
      : newHeight > lineHeight * 1.5;

    setHasMultipleLines(wrapsCompactTextarea);

    if (newHeight >= 400) textarea.scrollTop = textarea.scrollHeight;
  }, [question, isExpanded, isDictating]);

  /** 
   * Handle the user pasting an image
  **/
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    // Check for text/html clipboard item with embedded image (Teams, Outlook, etc.)
    let hasText = false;
    let foundImage = false;
    for (let i = 0; i < items.length; i++) {
      // Plaintext
      if (items[i].type === 'text/plain') hasText = true;
      else if (items[i].type === 'text/html') {
        items[i].getAsString(async (html) => {
          // Look for <img src="data:image/..."> in the HTML
          const match = html.match(/<img[^>]+src=["'](data:image\/(png|jpeg|jpg|gif);base64,[^"'>]+)["']/i);
          if (match && match[1]) {
            try {
              const dataUrl = match[1];
              // Parse the mime type and base64 data
              const mimeMatch = dataUrl.match(/^data:(image\/(png|jpeg|jpg|gif));base64,(.*)$/i);
              if (mimeMatch) {
                const mimeType = mimeMatch[1];
                const base64Data = mimeMatch[3];
                // Convert base64 to binary
                const byteString = atob(base64Data);
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let j = 0; j < byteString.length; j++) {
                  ia[j] = byteString.charCodeAt(j);
                }
                const file = new File([ab], 'upload.' + mimeType.split('/')[1], { type: mimeType });
                if (!validateImage(file, appStateContext)) {
                  showImageValidationError(appStateContext);
                  return;
                }
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                if (imageUploadRef.current) {
                  imageUploadRef.current.files = dataTransfer.files;
                  appStateContext?.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: imageUploadRef });
                  appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: '' });
                  textareaRef.current?.focus();
                }
                foundImage = true;
                e.preventDefault();
              }
            } catch (err) {
              // Fallback to default paste
            }
          }
        });
      }
    }
    // If plaintext or HTML image was found, do not proceed
    if (hasText || foundImage) return;
    // If no text/html image, check for direct image clipboard item
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          if (!validateImage(file, appStateContext)) {
            showImageValidationError(appStateContext);
            return;
          }
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          if (imageUploadRef.current) {
            imageUploadRef.current.files = dataTransfer.files;
            appStateContext?.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: imageUploadRef });
            appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: '' });
            textareaRef.current?.focus();
          }
          e.preventDefault();
          break;
        }
      }
    }
  }

  /** 
   * Handle sending prompt
  **/
  const handleSend = (transcribed='') => {
    if (disabled || fileUploadStateRef.current.isPreparing || recording || processingRecording || (!question.trim() && !transcribed)) return;

    let prompt = question.trim();
    if (!question.trim() && transcribed) prompt = transcribed;

    if (conversationId) sendPrompt(prompt, conversationId);
    else sendPrompt(prompt);

    setQuestion('');

    // Reset the textarea's height immediately
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  /** 
   * Handle file upload
  **/
  const isAllowedFile = (file: File) => {
    if (getAllowedFileMimeTypes().includes(file.type)) return true;
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    return getAllowedFileTypes().includes(extension);
  };

  const showFileReadError = (error: unknown) => {
    console.error('Could not prepare selected file upload:', error);
    if (isMountedRef.current) alert(error instanceof FileSnapshotError ? error.message : 'Could not prepare selected files. Please select them again.');
  };

  const onFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (fileUploadStateRef.current.isPreparing) {
      e.target.value = '';
      return;
    }
    appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: '' });
    if (!e.target.files) return;

    // Get files arrays
    const input = e.target;
    const selectedFiles = input.files;
    if (!selectedFiles) return;
    const newFiles = Array.from(selectedFiles).filter(isAllowedFile);
    const existingFiles = pendingFilesRef.current;

    // Single image - move to imageUpload
    if (existingFiles.length === 0 && newFiles.length === 1 && newFiles[0].type.startsWith('image/')) {
      fileUploadStateRef.current.invalidateImagePreparations();
      const selectionRevision = fileUploadStateRef.current.imageSelectionRevision;
      if (!validateImage(newFiles[0], appStateContext)) {
        showImageValidationError(appStateContext);
        input.value = '';
        return;
      }

      input.value = '';
      fileUploadStateRef.current.beginPreparation();
      try {
        const snapshot = await snapshotFile(newFiles[0]);
        if (selectionRevision === fileUploadStateRef.current.imageSelectionRevision && imageUploadRef.current) {
          const imageTransfer = createFileList([snapshot]);
          imageUploadRef.current.files = imageTransfer.files;
          appStateContext?.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: imageUploadRef });
        }
      } catch (error) {
        showFileReadError(error);
      } finally {
        fileUploadStateRef.current.endPreparation();
      }
      textareaRef.current?.focus();
      return;
    }

    // Combine & remove duplicates by name/size/lastModified
    const combinedFiles = [...existingFiles, ...newFiles].filter(
      (file, index, files) =>
        index === files.findIndex(
          (candidate) =>
            candidate.name === file.name &&
            candidate.size === file.size &&
            candidate.lastModified === file.lastModified
        )
    );

    // Validate files
    const remainingSlots = Math.max(0, FILE_UPLOAD_CONSTRAINTS.MAX_FILES - (appStateContext?.state.individualFileChat?.length || 0));
    const result = validateFiles(combinedFiles, remainingSlots);

    // If there are any invalid files, show errors
    if (result.oversized.length > 0 || result.overLimit.length > 0) showFileValidationErrors(result);

    const filesToSnapshot = result.valid.filter((file) => !existingFiles.includes(file));
    const selectionRevision = fileUploadStateRef.current.fileSelectionRevision;
    input.value = '';
    if (filesToSnapshot.length === 0) {
      textareaRef.current?.focus();
      return;
    }

    fileUploadStateRef.current.beginPreparation();
    try {
      const snapshots = await snapshotFiles(filesToSnapshot);
      if (selectionRevision !== fileUploadStateRef.current.fileSelectionRevision) return;

      // Revalidate against the latest selection so overlapping picker reads
      // cannot exceed the count limit or overwrite a concurrent selection.
      const currentFiles = pendingFilesRef.current;
      const nextResult = validateFiles([...currentFiles, ...snapshots], remainingSlots);
      if (nextResult.oversized.length > 0 || nextResult.overLimit.length > 0) showFileValidationErrors(nextResult);
      fileUploadStateRef.current.files = createFileList(nextResult.valid).files;
      appStateContext?.dispatch({ type: 'SET_FILE_UPLOAD', payload: fileUploadStateRef });
    } catch (error) {
      showFileReadError(error);
    } finally {
      fileUploadStateRef.current.endPreparation();
    }

    textareaRef.current?.focus();
  }

  /** 
   * Handle removing file upload
  **/
  const handleRemoveFileUpload = (file: File) => {
    fileUploadStateRef.current.invalidateFilePreparations();
    // Check if file exists in fileUpload
    const fileUploadFiles = appStateContext?.state.fileUpload?.current?.files;
    if (fileUploadFiles && fileUploadFiles.length > 0) {
      const filesArray = Array.from<File>(fileUploadFiles);
      if (filesArray.includes(file)) {
        // Remove file from fileUpload
        const remainingFiles = filesArray.filter(f => f !== file);
        const dataTransfer = createFileList(remainingFiles);
        fileUploadStateRef.current.files = dataTransfer.files;
        appStateContext?.dispatch({ type: 'SET_FILE_UPLOAD', payload: fileUploadStateRef });
        return;
      }
    }
  }

  /** 
   * Handle image upload
  **/
  const onImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (fileUploadStateRef.current.isPreparing) {
      e.target.value = '';
      return;
    }
    appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: '' });
    if (e.target.files && e.target.files.length > 0) {
      const input = e.target;
      const selectedFile = input.files?.[0];
      if (!selectedFile) return;
      const image = selectedFile;
      if (!validateImage(image, appStateContext)) {
        showImageValidationError(appStateContext);
        input.value = '';
        return;
      }
      fileUploadStateRef.current.invalidateImagePreparations();
      const selectionRevision = fileUploadStateRef.current.imageSelectionRevision;
      input.value = '';
      fileUploadStateRef.current.beginPreparation();
      try {
        const snapshot = await snapshotFile(image);
        if (selectionRevision === fileUploadStateRef.current.imageSelectionRevision && imageUploadRef.current) {
          imageUploadRef.current.files = createFileList([snapshot]).files;
          appStateContext?.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: imageUploadRef });
        }
      } catch (error) {
        showFileReadError(error);
      } finally {
        fileUploadStateRef.current.endPreparation();
      }
      textareaRef.current?.focus();
    }
  }

  /** 
   * Handle removing image upload
  **/
  const handleRemoveImageUpload = (file: File) => {
    fileUploadStateRef.current.invalidateImagePreparations();
    // Check if file exists in imageUpload
    const imageUploadFiles = appStateContext?.state.imageUpload?.current?.files;
    if (imageUploadFiles && imageUploadFiles.length > 0) {
      const filesArray = Array.from<File>(imageUploadFiles);
      if (filesArray.includes(file)) {
        // Remove file from imageUpload
        const remainingFiles = filesArray.filter(f => f !== file);
        if (remainingFiles.length > 0) {
          const dataTransfer = createFileList(remainingFiles);
          if (imageUploadRef.current) {
            imageUploadRef.current.files = dataTransfer.files;
            appStateContext?.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: imageUploadRef });
          }
        } else {
          // Clear imageUpload if no files remain
          if (imageUploadRef.current) {
            imageUploadRef.current.value = '';
            appStateContext?.dispatch({ type: 'SET_IMAGE_UPLOAD', payload: imageUploadRef });
          }
        }
      }
    }
  }

  /** 
   * Handle starting audio recording
  **/
  const onStartRecording = async () => {
    if (disabled || recording || processingRecording || isVoiceSessionActive) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];
      cancelledRecordingRef.current = false;
      dictationPendingFocusRef.current = true;
      const mediaRecorder = new MediaRecorder(stream);
      setMicStream(stream);
      setRecording(true);

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        if (cancelledRecordingRef.current) {
          cancelledRecordingRef.current = false;
          audioChunksRef.current = [];
          return;
        }

        setProcessingRecording(true);
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
          const formData = new FormData();
          formData.append('file', audioBlob, 'audio.wav');
          const response = await transcribe(formData);

          if (response && response.ok) {
            const data = await response.json();
            const transcribedText = data.res.text;

            if (transcribedText && !isEmpty(transcribedText)) setQuestion(transcribedText);
          }
        } catch (error) {
          console.error('Failed to transcribe recorded audio.', error);
        } finally {
          setProcessingRecording(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      handleTrackEvent('click', 'start_recording', 'Start Recording', '', '', '');
    } catch (error) {
      console.error('Failed to start audio recording.', error);
      setMicStream(null);
      setRecording(false);
    }
  }

  /** 
   * Handle stopping audio recording
  **/
  const onStopRecording = () => {
    setRecording(false);
    setMicStream(null);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();

    // Stop all audio tracks to release the microphone
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }

  /** 
   * Handle discarding a recording without transcribing it
  **/
  const onCancelRecording = () => {
    if (!recording) return;

    cancelledRecordingRef.current = true;
    onStopRecording();
    handleTrackEvent('click', 'cancel_recording', 'Cancel Recording', '', '', '');
  }

  /** 
   * Handle Escape cancelling an in-progress recording
  **/
  useEffect(() => {
    if (!recording) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onCancelRecording();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [recording]);

  /** 
   * Handle the user going quiet after dictating, so an abandoned mic does not run on
  **/
  const onDictationSilence = () => {
    if (!recording) return;

    handleTrackEvent('auto', 'dictation_silence_stop', 'Dictation - Silence Stop', '', '', '');
    onStopRecording();
  }

  /** 
   * Stop a runaway recording so it never reaches transcription as a huge upload
  **/
  useEffect(() => {
    if (!recording) return;

    const timeout = window.setTimeout(() => {
      handleTrackEvent('auto', 'dictation_max_length_stop', 'Dictation - Max Length Stop', '', '', '');
      onStopRecording();
    }, MAX_RECORDING_MS);

    return () => window.clearTimeout(timeout);
  }, [recording]);

  /** 
   * Hand focus back to the composer once the textarea has remounted after dictation,
   * with the caret after the transcript rather than in front of it
  **/
  useEffect(() => {
    if (isDictating || !dictationPendingFocusRef.current) return;

    dictationPendingFocusRef.current = false;

    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.focus();
    const caret = textarea.value.length;
    textarea.setSelectionRange(caret, caret);
  }, [isDictating]);

  const isDesktop = typeof window !== 'undefined' ? window.innerWidth > 700 : true;
  // Soft keyboards put Return where a newline is expected, so Enter only sends
  // where a physical keyboard is the primary input.
  const sendOnEnter = typeof window === 'undefined' || !window.matchMedia('(pointer: coarse)').matches;
  const hasConversationFiles = ((appStateContext?.state.individualFileChat || []).length || 0) > 0 && isEmpty(appStateContext?.state.currentChat?.filename);
  const composerPlaceholder = (selectedTool === 'image_generation' || (isExpanded && isImageGenerationMode)) ? 'Describe an image' : placeholder;
  const maxLength = isImageGenerationMode ? 3950 : 30000;
  const isPromptAtLimit = question.length >= maxLength;
  const sendProgress = (question.length / maxLength) * 100;
  const sendProgressDegrees = (sendProgress / 100) * 360;
  const sendProgressColor = sendProgress < 50 ? '#0076bf' : sendProgress < 75 ? '#eab308' : sendProgress < 90 ? '#f97316' : '#ef4444';
  const sendRingBackground = sendProgress === 0 ? 'transparent' : `conic-gradient(${sendProgressColor} ${sendProgressDegrees}deg, transparent ${sendProgressDegrees}deg)`;
  const toolbarPillBase = 'inline-flex h-8 items-center gap-2 rounded-full border border-black/10 px-3 text-[14px] text-black/72 transition hover:bg-black/[0.04] dark:border-white/10 dark:text-white/82 dark:hover:bg-white/[0.05]';
  const toolbarPillActive = 'border-sky-500/25 bg-sky-500/12 text-sky-700 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-300';
  const suggestionPillBase = 'inline-flex items-center gap-2 rounded-full border border-black/10 bg-transparent px-5 py-2 text-[15px] text-black/80 transition hover:bg-black/[0.04] dark:border-white/10 dark:text-white/90 dark:hover:bg-white/[0.04]';
  const canShowPopupMenu = Boolean(
    appStateContext?.state.currentModel?.files_allowed ||
    appStateContext?.state.currentModel?.images_allowed
  );
  const showIdleSuggestions = isDesktop && !appStateContext?.state.chatPanelTransitioned && isEmpty(appStateContext?.state.currentChat) && isEmpty(appStateContext?.state.currentCustomGPT) && isEmpty(appStateContext?.state.currentAgent);

  const toolLabelMap: Record<string, string> = {
    cgpt_selector: 'All Spaces',
    ms_office_data: 'Microsoft data',
    web_search: 'Web search',
    image_generation: 'Image'
  };

  const activeToolLabel = selectedTool ? toolLabelMap[selectedTool] ?? selectedTool : '';
  const renderMicTrigger = () => {
    if (recording) {
      return (
        <button type="button" aria-label="Finish dictation" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-500/20 text-red-500 transition hover:bg-red-500/30 dark:text-red-300" onClick={onStopRecording}>
          <MicOff size={18} />
        </button>
      );
    }

    if (processingRecording) {
      return (
        <button type="button" aria-label="Transcribing" disabled className={`${chromeButtonClass} opacity-40`}>
          <Mic size={18} />
        </button>
      );
    }

    return (
      <button type="button" aria-label="Dictate" className={`${chromeButtonClass} tour-step-13`} onClick={() => { void onStartRecording(); }}>
        <Mic size={18} />
      </button>
    );
  };

  const isInputEmpty = !question.trim();

  const renderSendOrVoice = () => {
    if (disabled) {
      return (
        <div className="inline-flex rounded-full p-[3px]">
          <button type="button" className={abortButtonClass} onClick={handleAbort}>
            <Square size={20} strokeWidth={0} fill="#000" />
          </button>
        </div>
      );
    }

    if (isInputEmpty && voiceModeEnabled && onOpenVoicePanel) {
      if (isVoiceSessionActive) {
        return (
          <div className="inline-flex rounded-full p-[3px]">
            <button type="button" aria-label="Open voice mode" className={`${sendButtonClass} bg-sky-500 dark:bg-sky-400`} onClick={onOpenVoicePanel} disabled={hasExhaustedWeeklyTokens || isPreparingFileUpload}>
              <AudioLines size={20} strokeWidth={2.5} />
            </button>
          </div>
        );
      }
      return (
        <div className="inline-flex rounded-full p-[3px] tour-step-14">
          <button type="button" aria-label="Start voice mode" className={sendButtonClass} onClick={onOpenVoicePanel} disabled={hasExhaustedWeeklyTokens || isPreparingFileUpload}>
            <AudioLines size={20} strokeWidth={2.5} />
          </button>
        </div>
      );
    }

    return (
      <div className="inline-flex rounded-full p-[3px]" style={{ background: sendRingBackground }}>
        <button type="button" onClick={() => handleSend()} className={sendButtonClass} disabled={hasExhaustedWeeklyTokens || isPreparingFileUpload}>
          {processingRecording ? <span className="loading loading-spinner" style={{ width: '22px' }}></span> : <ArrowUp size={20} strokeWidth={2.5} />}
        </button>
      </div>
    );
  };

  const activeToolIcon = (() => {
    if (selectedTool === 'cgpt_selector') return <Telescope size={15} className="shrink-0" />;
    if (selectedTool === 'ms_office_data') return <img src={microsoft365} width="15" className="shrink-0" />;
    if (selectedTool === 'web_search') return <Globe size={15} className="shrink-0" />;
    if (selectedTool === 'image_generation' || isImageGenerationMode) return <Image size={15} className="shrink-0" />;
    return null;
  })();
  const activeAgentIcon = (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="shrink-0" aria-label=""><path d="M7.45996 14.375C7.45996 13.3616 6.63844 12.54 5.625 12.54C4.61156 12.54 3.79004 13.3616 3.79004 14.375C3.79004 15.3884 4.61156 16.21 5.625 16.21C6.63844 16.21 7.45996 15.3884 7.45996 14.375ZM16.21 14.375C16.21 13.3616 15.3884 12.54 14.375 12.54C13.3616 12.54 12.54 13.3616 12.54 14.375C12.54 15.3884 13.3616 16.21 14.375 16.21C15.3884 16.21 16.21 15.3884 16.21 14.375ZM7.45996 5.625C7.45996 4.61156 6.63844 3.79004 5.625 3.79004C4.61156 3.79004 3.79004 4.61156 3.79004 5.625C3.79004 6.63844 4.61156 7.45996 5.625 7.45996C6.63844 7.45996 7.45996 6.63844 7.45996 5.625ZM16.21 5.625C16.21 4.61156 15.3884 3.79004 14.375 3.79004C13.3616 3.79004 12.54 4.61156 12.54 5.625C12.54 6.63844 13.3616 7.45996 14.375 7.45996C15.3884 7.45996 16.21 6.63844 16.21 5.625ZM17.54 14.375C17.54 16.123 16.123 17.54 14.375 17.54C12.627 17.54 11.21 16.123 11.21 14.375C11.21 12.627 12.627 11.21 14.375 11.21C16.123 11.21 17.54 12.627 17.54 14.375ZM8.79004 5.625C8.79004 7.37298 7.37298 8.79004 5.625 8.79004C3.87702 8.79004 2.45996 7.37298 2.45996 5.625C2.45996 3.87702 3.87702 2.45996 5.625 2.45996C7.37298 2.45996 8.79004 3.87702 8.79004 5.625ZM17.54 5.625C17.54 7.37298 16.123 8.79004 14.375 8.79004C13.7416 8.79004 13.153 8.60173 12.6582 8.28125L8.28125 12.6582C8.60173 13.153 8.79004 13.7416 8.79004 14.375C8.79004 16.123 7.37298 17.54 5.625 17.54C3.87702 17.54 2.45996 16.123 2.45996 14.375C2.45996 12.627 3.87702 11.21 5.625 11.21C6.25794 11.21 6.84623 11.3977 7.34082 11.7178L11.7178 7.34082C11.3977 6.84623 11.21 6.25794 11.21 5.625C11.21 3.87702 12.627 2.45996 14.375 2.45996C16.123 2.45996 17.54 3.87702 17.54 5.625Z"></path></svg>
  );

  const showImageControls = isImageGenerationMode && isExpanded;
  const showMarketingControl = !isEmpty(appStateContext?.state.currentCustomGPT) && appStateContext?.state.currentCustomGPT.id == 'e7e6d46d-dd49-4a08-b02a-4499de9d540a' && isExpanded;
  const selectedImageSizeOption = IMAGE_SIZE_OPTIONS.find((option) => option.value === imageSize) ?? IMAGE_SIZE_OPTIONS[0];
  const selectedImageStyleOption = IMAGE_STYLE_OPTIONS.find((option) => option.value === imageStyle) ?? IMAGE_STYLE_OPTIONS[0];
  const selectedMarketingOption = marketingSearchLevel === 'high'
    ? { value: 'high', label: 'Intensive', Icon: ClockAlert }
    : { value: 'medium', label: 'Regular', Icon: Clock1 };
  const optionMenuContentClass = 'w-[175px] rounded-[22px] border border-black/10 bg-secondary p-1.5 text-black shadow-short dark:border-white/10 dark:text-white';
  const optionMenuItemClass = 'flex w-full cursor-pointer items-center justify-between rounded-[16px] px-2.5 py-1.5 text-left transition hover:bg-black/[0.04] focus:bg-black/[0.04] dark:hover:bg-white/[0.05] dark:focus:bg-white/[0.05]';
  const optionMenuLabelClass = 'px-2.5 pb-1.5 pt-1 text-[11px] text-black/55 dark:text-white/75';
  const optionMenuItemTitleClass = 'text-[13px] text-black dark:text-white';
  const optionMenuItemDescriptionClass = 'text-xs text-black/45 dark:text-white/55';
  const iconMutedClass = 'text-black/55 dark:text-white/75';
  const chevronMutedClass = 'text-black/40 dark:text-white/55';
  const attachmentBadgeClass = 'flex items-center gap-2 rounded-full border border-black/10 bg-black/[0.03] px-3 py-2 text-xs text-black/75 dark:border-white/10 dark:bg-white/[0.03] dark:text-white/85';
  const attachmentRemoveClass = 'shrink-0 cursor-pointer text-black/40 transition hover:text-red-500 dark:text-white/55 dark:hover:text-red-400';
  const chromeButtonClass = 'inline-flex h-9 w-9 items-center justify-center rounded-full text-black/70 transition hover:bg-black/[0.05] dark:text-white/80 dark:hover:bg-white/[0.05]';
  const abortButtonClass = 'inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/5 text-black/70 dark:bg-white/10 dark:text-white';
  const sendButtonClass = 'inline-flex h-10 w-10 items-center justify-center rounded-full bg-black text-white transition disabled:opacity-50 dark:bg-white dark:text-black';
  const promptLimitBarTone = 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:border-rose-400/25 dark:bg-rose-400/10 dark:text-rose-200';
  const longConversationBarTone = 'border-amber-500/25 bg-amber-500/10 text-amber-800 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-200';
  const composerShellClass = `overflow-hidden rounded-[28px] border border-black/5 bg-secondary shadow-[0_2px_8px_0_rgba(0,0,0,0.06)] transition-all duration-300 ease-out dark:border-white/10 dark:shadow-[0_2px_10px_0_rgba(0,0,0,0.16)] ${isExpanded ? 'px-3 py-3.5' : 'px-3 py-2.5'}`;

  const attachmentBadges = (
    <>
      {uploadedFiles.length > 0 ?
        <div className="flex flex-wrap gap-2">
          {uploadedFiles.map((file, idx) =>
            <div key={`${file.name}-${idx}`} className={attachmentBadgeClass}>
              <div className="flex items-center gap-2 min-w-0">
                <img src={getFileIcon(file.name)} width="18" />
                <span className="truncate max-w-[180px]">{file.name}</span>
              </div>
              <CircleX size={15} className={attachmentRemoveClass} onClick={() => { handleRemoveFileUpload(file); handleTrackEvent('click', 'conversation_files_remove', 'Conversation Files - Remove File', '', '', ''); }} />
            </div>
          )}
        </div>
      : null}

      {uploadedImages.length > 0 ?
        <div className="flex flex-wrap gap-2">
          {uploadedImages.map((file, idx) =>
            <div key={`${file.name}-${idx}`} className={attachmentBadgeClass}>
              <div className="flex items-center gap-2 min-w-0">
                <img src={getFileIcon(file.name)} width="18" />
                <span className="truncate max-w-[180px]">{file.name}</span>
              </div>
              <CircleX size={15} className={attachmentRemoveClass} onClick={() => { handleRemoveImageUpload(file); handleTrackEvent('click', 'conversation_files_remove', 'Conversation Files - Remove Image', '', '', ''); }} />
            </div>
          )}
        </div>
      : null}
    </>
  );

  return (
    <div className="flex flex-col w-full cursor-text prompt-input max-[700px]:px-2 max-[700px]:pb-2">
      {hasExhaustedWeeklyTokens ?
        <div role="status" className="mx-auto mb-2 flex items-start gap-3 rounded-3xl border border-amber-500/30 bg-amber-500/10 py-4 px-5 text-sm leading-snug text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
          <TriangleAlert size={17} className="mt-0.5 shrink-0" />
          <span>Weekly token limit reached. Resets {formatNextWeeklyReset()}.</span>
        </div>
      : null}

      {selectedTool === 'web_search' ?
        <div className="flex justify-center pb-2">
          <div className="flex items-start gap-2 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
            <TriangleAlert size={14} className="mt-[1px] shrink-0" />
            <span>Web search sends your prompt to multiple third-party search providers. Do not include confidential or internal information.</span>
          </div>
        </div>
      : null}

      {/* Docked status bar — tucks behind the top edge of the composer shell */}
      {(isPromptAtLimit || longConversation) ?
        <div className={`mx-auto -mb-5 flex w-[92%] items-center gap-2 rounded-t-[20px] border border-b-0 px-4 pb-6 pt-1.5 text-xs leading-snug ${isPromptAtLimit ? promptLimitBarTone : longConversationBarTone}`}>
          <TriangleAlert size={13} className="shrink-0" />
          <span>{isPromptAtLimit ? 'Your prompt has reached the maximum character length.' : 'This conversation has gotten long — consider starting a new one to limit token usage.'}</span>
        </div>
      : null}

      <div className={`relative ${composerShellClass}`} onClick={() => textareaRef.current?.focus()}>
        {disabled ?
          <svg
            className="pointer-events-none absolute inset-0 z-50 h-full w-full"
          >
            <defs>
              <filter id="borderGlow">
                <feGaussianBlur stdDeviation="1.5" />
              </filter>
            </defs>

            <rect
              x="0.5"
              y="0.5"
              width="100%"
              height="99%"
              rx="28"
              fill="none"
              stroke={appStateContext?.state.theme == 'dark' ? "#bfbfbf" : "#626479"}
              strokeWidth="1"
              strokeLinecap="round"
              strokeDasharray="8 92"
              pathLength="100"
              filter="url(#borderGlow)"
            >
              <animate
                attributeName="stroke-dashoffset"
                from="100"
                to="0"
                dur="2s"
                repeatCount="indefinite"
              />
            </rect>
          </svg>
        : null}

        <input type="file" className="hidden" id="file" ref={fileUploadRef} accept={getAllowedFileTypes().join(',')} multiple onChange={(e) => onFileUpload(e)} />
        <input type="file" className="hidden" id="file" ref={imageUploadRef} accept={FILE_UPLOAD_CONSTRAINTS.ALLOWED_IMAGE_TYPES.join(',')} onChange={(e) => onImageUpload(e)} />

        <div className="flex flex-col gap-3">
          {hasUploads ? attachmentBadges : null}
          {isPreparingFileUpload ? <div role="status" className="px-1 text-xs text-black/55 dark:text-white/60">Preparing selected files…</div> : null}

          <div className={`grid items-center gap-3 transition-all duration-300 ease-out ${isExpanded ? 'grid-cols-[1fr_auto]' : 'grid-cols-[auto_1fr_auto]'}`}>
            {!isExpanded ?
              <div className="flex items-center justify-center tour-step-11">
                {canShowPopupMenu ? <PopupMenu /> : null}
              </div>
            : null}

            <div className={`flex items-center min-w-0 ${isExpanded ? 'col-span-2' : ''}`}>
              {isDictating ?
                <DictationIndicator stream={micStream} transcribing={processingRecording} onSilenceTimeout={onDictationSilence} />
              :
                <textarea
                  ref={textareaRef}
                  className={`w-full resize-none border-0 bg-transparent px-1 text-[15px] [@media(pointer:coarse)]:text-[16px] leading-6 text-black placeholder:text-black/35 shadow-none outline-none ring-0 transition-all duration-300 ease-out focus:border-0 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-white/35 ${isExpanded ? 'pt-0.5' : 'h-8 py-1'} tour-step-10`}
                  style={{
                    resize: 'none',
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                    outline: 'none',
                    boxShadow: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                  rows={1}
                  autoComplete="off"
                  placeholder={composerPlaceholder}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onFocus={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.outline = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  enterKeyHint={sendOnEnter ? 'send' : 'enter'}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                    if (!sendOnEnter) return;
                    e.preventDefault();
                    handleSend();
                  }}
                  maxLength={maxLength}
                  onPaste={handlePaste}
                />
              }
            </div>

            {!isExpanded ?
              <div className="flex items-center gap-1">
                <ModelSelector
                  currentModel={appStateContext?.state.currentModel || null}
                  onModelChange={handleModelChange}
                  onEffortChange={handleEffortChange}
                  getBrandLogo={handleGetLogo}
                  onTrackEvent={(item, text) => handleTrackEvent('click', item, text, '', '', '')}
                  hideLabelOnMobile
                />

                {renderMicTrigger()}

                {renderSendOrVoice()}
              </div>
            : null}
          </div>

          {isExpanded ?
            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="flex min-w-0 items-center gap-2 overflow-x-auto scrollbar-none">
                {canShowPopupMenu ? <PopupMenu /> : null}

                {selectedTool ?
                  <button
                    type="button"
                    className={`${toolbarPillBase} ${toolbarPillActive} group shrink-0`}
                    onClick={() => appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: null })}
                  >
                    <span className="inline-flex items-center">
                      <span className="group-hover:hidden">
                        {activeToolIcon}
                      </span>
                      <X size={15} className="hidden rounded-full bg-sky-500/15 p-0.5 text-sky-700 group-hover:block dark:bg-sky-400/20 dark:text-sky-200" />
                    </span>
                    <span>{selectedTool === 'image_generation' ? 'Image' : activeToolLabel}</span>
                  </button>
                : null}

                {showImageControls ?
                  <>
                    <DropdownMenu open={isImageSizeMenuOpen} onOpenChange={setIsImageSizeMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className={`${toolbarPillBase} shrink-0`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrackEvent('click', 'image_size', 'Image Size', '', '', '');
                          }}
                        >
                          <selectedImageSizeOption.Icon size={15} className={`shrink-0 ${iconMutedClass}`} />
                          <span>{selectedImageSizeOption.label}</span>
                          <ChevronDown size={14} className={chevronMutedClass} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className={optionMenuContentClass} align="start" alignOffset={0} sideOffset={10}>
                        <div className={optionMenuLabelClass}>Choose image size</div>
                        {IMAGE_SIZE_OPTIONS.map((option) => (
                          <DropdownMenuItem
                            key={option.value}
                            className={`${optionMenuItemClass} ${selectedImageSizeOption.value === option.value ? 'text-sky-700 dark:text-sky-300' : ''}`}
                            onClick={() => {
                              setImageSize(option.value);
                              setIsImageSizeMenuOpen(false);
                            }}
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <option.Icon size={16} className="shrink-0" />
                              <div className="min-w-0">
                                <div className={selectedImageSizeOption.value === option.value ? 'text-[13px]' : optionMenuItemTitleClass}>{option.label}</div>
                                <div className={optionMenuItemDescriptionClass}>{option.description}</div>
                              </div>
                            </div>
                            {selectedImageSizeOption.value === option.value ? <Check size={16} className="shrink-0" /> : null}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu open={isImageStyleMenuOpen} onOpenChange={setIsImageStyleMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className={`${toolbarPillBase} shrink-0`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTrackEvent('click', 'image_style', 'Image Style', '', '', '');
                          }}
                        >
                          <Palette size={15} className={`shrink-0 ${iconMutedClass}`} />
                          <span>{selectedImageStyleOption.label}</span>
                          <ChevronDown size={14} className={chevronMutedClass} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className={optionMenuContentClass} align="start" alignOffset={0} sideOffset={10}>
                        <div className={optionMenuLabelClass}>Choose image style</div>
                        {IMAGE_STYLE_OPTIONS.map((option) => (
                          <DropdownMenuItem
                            key={option.value || 'auto'}
                            className={`${optionMenuItemClass} ${selectedImageStyleOption.value === option.value ? 'text-sky-700 dark:text-sky-300' : ''}`}
                            onClick={() => {
                              setImageStyle(option.value);
                              setIsImageStyleMenuOpen(false);
                            }}
                          >
                            <div className="min-w-0">
                              <div className={selectedImageStyleOption.value === option.value ? 'text-[13px]' : optionMenuItemTitleClass}>{option.label}</div>
                            </div>
                            {selectedImageStyleOption.value === option.value ? <Check size={16} className="shrink-0" /> : null}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                : null}

                {showMarketingControl ?
                  <DropdownMenu open={isMarketingMenuOpen} onOpenChange={setIsMarketingMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={`${toolbarPillBase} shrink-0`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTrackEvent('click', 'marketing_search_level', 'Marketing Search Level', '', '', '');
                        }}
                      >
                        <selectedMarketingOption.Icon size={15} className={`shrink-0 ${iconMutedClass}`} />
                        <span>{selectedMarketingOption.label}</span>
                        <ChevronDown size={14} className={chevronMutedClass} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className={optionMenuContentClass} align="start" alignOffset={0} sideOffset={10}>
                      <div className={optionMenuLabelClass}>Choose search level</div>
                      {[
                        { value: 'medium', label: 'Regular', Icon: Clock1 },
                        { value: 'high', label: 'Intensive', Icon: ClockAlert }
                      ].map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          className={`${optionMenuItemClass} ${marketingSearchLevel === option.value ? 'text-sky-700 dark:text-sky-300' : ''}`}
                          onClick={() => {
                            setMarketingSearchLevel(option.value);
                            setIsMarketingMenuOpen(false);
                          }}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <option.Icon size={16} className="shrink-0" />
                            <div className="min-w-0">
                              <div className={marketingSearchLevel === option.value ? 'text-[13px]' : optionMenuItemTitleClass}>{option.label}</div>
                            </div>
                          </div>
                          {marketingSearchLevel === option.value ? <Check size={16} className="shrink-0" /> : null}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                : null}

                {!selectedTool && !isEmpty(appStateContext?.state.currentAgent) ?
                  <button
                    type="button"
                    className={`${toolbarPillBase} ${toolbarPillActive} group shrink-0`}
                    onClick={() => appStateContext?.dispatch({ type: 'SET_CURRENT_AGENT', payload: null })}
                  >
                    <span className="inline-flex items-center">
                      <span className="group-hover:hidden">
                        {activeAgentIcon}
                      </span>
                      <X size={15} className="hidden rounded-full bg-sky-500/15 p-0.5 text-sky-700 group-hover:block dark:bg-sky-400/20 dark:text-sky-200" />
                    </span>
                    <span className="truncate max-w-[180px]">{appStateContext?.state.currentAgent.name}</span>
                  </button>
                : null}
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {hasConversationFiles ?
                  <div className="tooltip tooltip-bottom before:rounded-lg before:bg-black before:text-white before:text-xs relative" data-tip="Conversation Files">
                    <button type="button" className={`relative ${chromeButtonClass}`} onClick={() => { appStateContext?.dispatch({ type: 'SET_CONV_FILES_OPEN', payload: true }); handleTrackEvent('click', 'conversation_files_show', 'Conversation Files - Show', '', '', ''); }}>
                      <div className="absolute -left-1 top-0 flex min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] text-white"><strong>{((appStateContext?.state.individualFileChat || []).length || 0)}</strong></div>
                      <FileStack size={18} />
                    </button>
                  </div>
                : null}

                <ModelSelector
                  currentModel={appStateContext?.state.currentModel || null}
                  onModelChange={handleModelChange}
                  onEffortChange={handleEffortChange}
                  getBrandLogo={handleGetLogo}
                  onTrackEvent={(item, text) => handleTrackEvent('click', item, text, '', '', '')}
                />

                {renderMicTrigger()}

                {renderSendOrVoice()}
              </div>
            </div>
          : null}

        </div>
      </div>

      <div className="text-center text-xs text-gray-500 pt-1">AI can make mistakes. Verify outputs and disclose AI use when sharing this content.</div>

      {showIdleSuggestions ?
        <div className="flex flex-wrap items-center justify-center gap-3 pt-3">
          <button type="button" className={`${suggestionPillBase} tour-step-15`} onClick={() => handleSelectTool('image_generation')}>
            <Image size={16} className="shrink-0" />
            <span className="text-sm">Create an image</span>
          </button>
          {/* <button type="button" className={suggestionPillBase} onClick={() => handleSelectTool('cgpt_selector')}>
            <Telescope size={16} className="shrink-0" />
            <span className="text-sm">Custom GPT selector</span>
          </button> */}
          <button type="button" className={`${suggestionPillBase} tour-step-16`} onClick={() => handleSelectTool('ms_office_data')}>
            <img src={microsoft365} width="16" className="shrink-0" />
            <span className="text-sm">Microsoft data</span>
          </button>
          <button type="button" className={`${suggestionPillBase} tour-step-17`} onClick={() => handleSelectTool('web_search')}>
            <Globe size={16} className="shrink-0" />
            <span className="text-sm">Web search</span>
          </button>
        </div>
      : null}
    </div>
  );
}

export default PromptInput;
